import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import ReactLoading from "react-loading";

import * as helpers from "../../utils/utils.js";
import styles from "./Confirmation.module.css";

import { useBooking } from "../../hooks/useBooking";
import { useCustomer } from "../../hooks/useCustomer";
import { useClient } from "../../hooks/useClient";
import { useModal } from "../../hooks/useModal";

import Header from "../../components/header/header";
import SlotTableRow from "../../components/slotTableRow/slotTableRow";
import EditModal from "../../components/editModal/editModal";
import ArrivalTimeForm from "../../components/forms/arrivalTimeForm";
import Signature from "../../components/signature/signature";
import AddonForm from "../../components/forms/addOnForm.js";
import ErrorMessage from "../../components/shared/ErrorMessage.js";
import LicenseForm from "../../components/forms/licenseForm.js";
import CustomerNotes from "../../components/customerNotes/customerNotes.js";
import { Mail, MapPin, Phone, User } from "lucide-react";

const ADDONS = { tube: 50, pets: 30, moreTime: 80 };
const CHECK_IN_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

const getBookingStartTimestamp = (booking) => {
  const slotDates = (booking?.slots || [])
    .map((slot) => Number(slot?.date || slot?.startDate || slot?.start))
    .filter((date) => Number.isFinite(date) && date > 0);

  if (slotDates.length > 0) return Math.min(...slotDates);

  const fallbackDate = Number(booking?.firstDay || booking?.date || booking?.startDate);
  return Number.isFinite(fallbackDate) && fallbackDate > 0 ? fallbackDate : null;
};

const formatRentalDate = (timestamp) => {
  if (!timestamp) return "your rental";

  return new Date(timestamp).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

const getConfirmationTitle = ({ checkInIsOpen, checkInComplete }) => {
  if (!checkInIsOpen) return "Booking Confirmed!";
  return checkInComplete ? "You're All Set!" : "Complete Your Check-In";
};

const Confirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id") || searchParams.get("bookingId");
  const [hasPromptedCheckIn, setHasPromptedCheckIn] = useState(false);

  const { clientData, clientError, clientIsLoading } = useClient();
  const { getBookingById, updateBooking, contractsUrl, emailContracts } = useBooking();
  const { retrieveCustomerById } = useCustomer();

  const { show: showSignature, close: hideSignature, open: openSignature } = useModal();
  const { show: showAddonsModal, close: closeAddonsModal, open: openAddonsModal } = useModal();
  const { show: showLicenseModal, close: closeLicenseModal, open: openLicenseModal } = useModal();
  const { show: showParkingModal, close: closeParkingModal, open: openParkingModal } = useModal();
  const {
    show: showArrivalModal,
    close: closeArrivalModal,
    open: openArrivalModal,
  } = useModal();

  const {
    data: booking,
    error: bookingError,
    isLoading: bookingIsLoading,
  } = useQuery(["bookingData", bookingId], () => getBookingById(bookingId), {
    enabled: !!bookingId,
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60,
  });

  const {
    data: customer,
    error: customerError,
    isLoading: customerIsLoading,
  } = useQuery(["customerData", booking?.customerId], () => retrieveCustomerById(booking?.customerId), {
    enabled: !!booking?.customerId,
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60,
  });

  const bookingStartTimestamp = useMemo(() => getBookingStartTimestamp(booking), [booking]);
  const checkInIsOpen = Boolean(
    bookingStartTimestamp && bookingStartTimestamp - Date.now() <= CHECK_IN_WINDOW_MS
  );
  const checkInComplete = Boolean(booking?.signature && booking?.arrivalTime && booking?.hasLicense);
  const pageTitle = getConfirmationTitle({ checkInIsOpen, checkInComplete });
  const rentalDateLabel = formatRentalDate(bookingStartTimestamp);
  const numAddons = Object.keys(booking?.costs || {}).filter((key) => ADDONS[key]).length;

  useEffect(() => {
    if (!checkInIsOpen || !booking || hasPromptedCheckIn) return;

    if (!booking.arrivalTime) {
      openArrivalModal();
      setHasPromptedCheckIn(true);
      return;
    }

    if (!booking.hasLicense) {
      openLicenseModal();
      setHasPromptedCheckIn(true);
    }
  }, [booking, checkInIsOpen, hasPromptedCheckIn, openArrivalModal, openLicenseModal]);

  const handleUpdateArrival = async (time) => {
    const shouldPromptLicense = Boolean(time && checkInIsOpen && !booking?.hasLicense);

    if (time) {
      try {
        await updateBooking({
          id: bookingId,
          bookingData: { ...booking, arrivalTime: time },
        });
      } catch (error) {
        console.error("Error updating booking arrival time:", error);
      }
    }

    closeArrivalModal();

    if (shouldPromptLicense) {
      openLicenseModal();
    }
  };

  const handleSaveNote = async (note) => {
    await updateBooking({
      id: bookingId,
      bookingData: { ...booking, customerNotes: note },
    });
  };

  const handleSaveSignature = async (sigUrl) => {
    if (!sigUrl) return;

    const updatedBooking = {
      ...booking,
      signature: sigUrl,
      signatureTimestamp: new Date().toISOString(),
      signatureIp: "unknown",
    };

    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      updatedBooking.signatureIp = data.ip;
    } catch (ipError) {
      console.warn("Could not retrieve IP address:", ipError);
    }

    try {
      await updateBooking({
        id: bookingId,
        bookingData: updatedBooking,
      });

      await emailContracts.mutateAsync(bookingId);
    } catch (error) {
      console.error("Error updating booking signature:", error);
    }

    hideSignature();
  };

  const handleSaveLicense = async (licenseChecked) => {
    if (!licenseChecked) return;

    const updatedBooking = {
      ...booking,
      hasLicense: licenseChecked,
      hasLicenseTimestamp: new Date().toISOString(),
      hasLicenseIp: "unknown",
    };

    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      updatedBooking.hasLicenseIp = data.ip;
    } catch (ipError) {
      console.warn("Could not retrieve IP address:", ipError);
    }

    try {
      await updateBooking({
        id: bookingId,
        bookingData: updatedBooking,
      });
    } catch (error) {
      console.error("Error updating booking license requirement:", error);
    }

    closeLicenseModal();
  };

  const handleUpdateAddons = async (addonKeys) => {
    const costs = { ...(booking?.costs || {}) };

    addonKeys.forEach((key) => {
      costs[key] = { amount: ADDONS[key], paid: 0 };
    });

    Object.keys(ADDONS).forEach((key) => {
      if (!addonKeys.includes(key)) {
        delete costs[key];
      }
    });

    try {
      await updateBooking({
        id: bookingId,
        bookingData: { ...booking, costs },
      });
    } catch (error) {
      console.error("Error updating booking costs:", error);
    }

    closeAddonsModal();
  };

  if (bookingIsLoading || customerIsLoading || clientIsLoading) {
    return <div className="centering-container"><ReactLoading type="spin" color="#000" /></div>;
  }

  if (clientError || bookingError || customerError || !booking || !customer || !clientData) {
    return (
      <div className="centering-container">
        <ErrorMessage
          title="Oops! Unable to Load Booking"
          error="Missing booking details. Please return to the homepage."
          linkText="Back to Home"
          command={() => navigate(-1)}
        />
      </div>
    );
  }

  return (
    <>
      <div className={styles.page}>
        <Header
          name={clientData.companyName}
          websiteUrl={clientData.website}
          logoUrl={clientData.logo}
        />

        <div className={styles.content}>
          <div className={styles.hero}>
            <h1>{pageTitle}</h1>
            <p className={styles.subtitle}>
              {checkInIsOpen
                ? `Your rental is coming up on ${rentalDateLabel}. Finish the items below to make arrival smoother.`
                : `Thank you for your booking${customer?.firstName ? `, ${customer.firstName}` : ""}! Your rental is confirmed for ${rentalDateLabel}. Online check-in opens 3 days before your rental.`}
            </p>
            <div className={styles.confirmationNumber}>
              <span className={styles.confirmationLabel}>Confirmation #</span>
              <span className={styles.confirmationId}>{bookingId}</span>
            </div>
          </div>

          {checkInIsOpen ? (
            <section className="section">
              <h2>Before You Arrive</h2>

              {booking.signature ? (
                <div className="alert alert-success mb-3 d-flex align-items-center justify-content-between">
                  <div>Contract signed successfully. You should receive a copy by email.</div>
                  <div>
                    <button className="btn" onClick={() => window.open(contractsUrl(bookingId), "_blank")}>
                      View Contracts
                    </button>
                  </div>
                </div>
              ) : (
                <div className="alert alert-info mb-3 d-flex align-items-center justify-content-between">
                  <div>Review and sign the rental agreement before you arrive.</div>
                  <div className="d-flex flex-column flex-md-row">
                    <button
                      className="btn m-1"
                      onClick={() => window.open(contractsUrl(bookingId), "_blank")}
                    >
                      View Contracts
                    </button>
                    <button
                      className="btn btn-primary m-1"
                      onClick={() => openSignature()}
                    >
                      Sign Contract
                    </button>
                  </div>
                </div>
              )}

              <div className={`alert ${booking.arrivalTime ? "alert-success" : "alert-info"} mb-3 d-flex align-items-center justify-content-between`}>
                <div>
                  <strong>Arrival Time:</strong> {booking.arrivalTime || "Not selected"}
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={openArrivalModal}>
                  {booking.arrivalTime ? "Edit" : "Select"}
                </button>
              </div>
            </section>
          ) : (
            <section className="section">
              <h2>Check-In Opens Soon</h2>
              <div className="alert alert-info mb-3">
                Come back to this link within 3 days of your rental to sign the contract, confirm operator eligibility, and select your arrival time.
              </div>
            </section>
          )}

          <section className="section">
            <h2>Add-Ons</h2>
            {numAddons > 0 ? (
              <div className="alert alert-success mb-3 d-flex align-items-center justify-content-between">
                <div>You have selected {numAddons} add-on{numAddons > 1 ? "s." : "."}</div>
                <button
                  className="btn"
                  onClick={() => openAddonsModal()}
                >
                  View/Edit Add-Ons ({numAddons})
                </button>
              </div>
            ) : (
              <div className="alert alert-info mb-3 d-flex align-items-center justify-content-between">
                <div>Select any optional add-ons to enhance your experience.</div>
                <button
                  className="btn"
                  onClick={() => openAddonsModal()}
                >
                  View Add-Ons
                </button>
              </div>
            )}
          </section>

          <section className="section">
            <h2>Notes for Us</h2>
            <CustomerNotes
              initialNote={booking.customerNotes || ""}
              onSave={handleSaveNote}
            />
          </section>

          <section className="section">
            <h2>Arrival Instructions</h2>
            <ul>
              <li>
                Please plan to arrive at{" "}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clientData.address)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <strong>{clientData.address}</strong>
                </a>
                .
              </li>
              <li>
                Free private parking is available directly across the street. For your convenience, please review the{" "}
                <button type="button" className={styles.inlinePhotoButton} onClick={openParkingModal}>
                  Parking Location Photo
                </button>{" "}
                before arrival. Kindly call or text 5 minutes before you arrive so we can meet your group outside.
              </li>
              <li>Bring your ID and confirmation email.</li>
              <li>
                <strong>Weather policy:</strong> If we deem conditions unsafe, we will reschedule or refund your booking.
              </li>
              <li>
                <strong>Contact Us:</strong> If you have any questions, call or text us{" "}
                <strong>
                  <a href={"tel:" + clientData.phone}>
                    <span>{helpers.formatPhoneNumber(clientData.phone)}</span>
                  </a>
                </strong>.
              </li>
            </ul>
          </section>

          <section className="section">
            <h2>Booking Summary</h2>
            <div className="table-container table-responsive">
              <table className="table slot-table">
                <thead>
                  <tr>
                    <th className="slot-image">Image</th>
                    <th>Unit</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.slots &&
                    booking.slots.map((slot) => (
                      <tr key={slot.id} className="slot-row">
                        <SlotTableRow
                          slotId={slot.id}
                          onSelect={() => console.log("slot clicked")}
                          isSelected={false}
                        />
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="section">
            <h2>Pricing Summary</h2>
            <div className="table-container table-responsive">
              <table className="table slot-table">
                <thead>
                  <tr>
                    <th>Charge</th>
                    <th>Amount</th>
                    <th>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.costs &&
                    Object.keys(booking.costs).map((name) => (
                      <tr key={name} className="slot-row">
                        <td>{name.charAt(0).toUpperCase() + name.slice(1)}</td>
                        <td>${booking.costs[name].amount}</td>
                        <td>{booking.costs[name].amount === booking.costs[name].paid ? "YES" : "NO"}</td>
                      </tr>
                    ))}
                  {!booking.costs?.gas && (
                    <tr className="slot-row">
                      <td>Fuel Used</td>
                      <td>$0</td>
                      <td>NO</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="section">
            <h2>Your Info</h2>
            <div className={styles.customerSummary}>
              <div className={styles.customerHeader}>
                <div className={styles.customerIcon} aria-hidden="true">
                  <User size={22} />
                </div>
                <div>
                  <span>Renter</span>
                  <strong>{helpers.formatCustomerName(customer, "Renter")}</strong>
                </div>
              </div>

              <div className={styles.customerDetails}>
                {customer.email && (
                  <a className={styles.customerDetail} href={`mailto:${customer.email}`}>
                    <span>
                      <Mail size={15} aria-hidden="true" />
                      Email
                    </span>
                    <strong>{customer.email}</strong>
                  </a>
                )}

                {customer.phone && (
                  <a className={styles.customerDetail} href={`tel:${String(customer.phone).replace(/\D/g, "")}`}>
                    <span>
                      <Phone size={15} aria-hidden="true" />
                      Phone
                    </span>
                    <strong>{helpers.formatCustomerPhone(customer.phone)}</strong>
                  </a>
                )}

                <div className={styles.customerDetail}>
                  <span>
                    <MapPin size={15} aria-hidden="true" />
                    Address
                  </span>
                  <strong>{helpers.formatCustomerAddress(customer, "Not provided")}</strong>
                </div>
              </div>
            </div>
          </section>

          <div className="h-center-container">
            <button className="btn" onClick={() => navigate(`/${clientData.id}`)}>Return To Booking Calendar</button>
          </div>
        </div>

        <EditModal
          title="Set Desired Arrival Time"
          show={showArrivalModal}
          onClose={closeArrivalModal}
        >
          <ArrivalTimeForm
            initialData={booking.arrivalTime || "10:00"}
            min="09:45"
            max="20:00"
            onSubmit={handleUpdateArrival}
          />
        </EditModal>

        <EditModal
          title="Confirm Operator License"
          show={showLicenseModal}
          onClose={closeLicenseModal}
        >
          <LicenseForm
            initialLicense={booking.hasLicense ?? false}
            onSubmit={handleSaveLicense}
          />
        </EditModal>

        <EditModal title="Signature" show={showSignature} onClose={() => hideSignature()}>
          <Signature onSubmit={handleSaveSignature} />
        </EditModal>

        <EditModal title="Parking Location Photo" show={showParkingModal} onClose={closeParkingModal}>
          <img
            className={styles.parkingPhoto}
            src="/parking.jpg"
            alt="Parking location"
          />
        </EditModal>

        <EditModal title="Select Add-Ons" show={showAddonsModal} onClose={() => closeAddonsModal()}>
          <AddonForm
            initialAddons={Object.keys(booking?.costs || {}).filter((key) => ADDONS[key])}
            onSubmit={(addons) => handleUpdateAddons(addons)}
          />
        </EditModal>
      </div>
    </>
  );
};

export default Confirmation;
